import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import GlobalSearch from "@/components/GlobalSearch";
import { CartButton } from "@/components/marketplace/CartDrawer";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, Shield, User, Users, Building2, CreditCard, Bell, Settings, Radio, Megaphone, ClipboardList, Search } from "lucide-react";

// Public navigation — organized around what a visitor wants to do, not internal software structure.
const NAV = [
  { label: "Home", to: "/", end: true },
  {
    label: "Prospects",
    children: [
      { label: "Search Prospects", to: "/prospects" },
      { label: "CBR Nation Alumni", to: "/cbr-nation-alumni" },
      { label: "Rankings", to: "/rankings" },
      { label: "Commitments", to: "/commitments" },
      { label: "Leaderboards", to: "/leaderboards" },
      { label: "Movers & Risers", to: "/movers" },
      { label: "Recruiter Portal", to: "/recruiter" },
    ],
  },
  {
    label: "Rankings",
    children: [
      { label: "Team Rankings", to: "/live/team-rankings" },
      { label: "Team Comparison", to: "/live/compare" },
      { label: "How CBR Team Rankings Work", to: "/live/team-rankings/how-it-works" },
      { label: "Trending on CBR", to: "/trending" },
      { label: "Provincial Rankings", comingSoon: true },
      { label: "League Rankings", comingSoon: true },
      { label: "Historical Rankings", comingSoon: true },
    ],
  },
  { label: "Camps", to: "/camps" },
  { label: "Shop", to: "/marketplace" },
  {
    label: "CBR Live",
    children: [
      { label: "Home", to: "/live" },
      { label: "Schedule", to: "/live/schedule" },
      { label: "Live Games", comingSoon: true },
      { label: "Organizations", to: "/organizations" },
      { label: "Teams", to: "/teams" },
    ],
  },
  {
    label: "Organizations",
    children: [
      { label: "Organizations", to: "/organizations" },
      { label: "Search Teams", to: "/teams" },
      { label: "Claim Ownership", to: "/live/claim" },
      { label: "Founding Members", comingSoon: true },
      { label: "Verified Organizations", comingSoon: true },
    ],
  },
  {
    label: "Membership",
    children: [
      { label: "Membership Plans", to: "/membership" },
      { label: "Launch Promotion", to: "/membership" },
    ],
  },
  {
    label: "About",
    children: [
      { label: "About CBR", to: "/about" },
      { label: "CBR Nation", comingSoon: true },
      { label: "News", to: "/news" },
      { label: "Our Sponsors", to: "/sponsors" },
      { label: "Become a Sponsor", to: "/sponsorship" },
    ],
  },
];

// Authenticated account menu — "what do I need to do today?"
const ACCOUNT_LINKS = [
  { label: "Command Center", to: "/command-center", icon: LayoutDashboard, testid: "menu-command-center-link" },
  { label: "My Profile", to: "/my-profile", icon: User, testid: "menu-profile-link" },
  { label: "My Teams", to: "/live/account", icon: Users, testid: "menu-teams-link" },
  { label: "My Organization", to: "/live/account", icon: Building2, testid: "menu-org-link" },
  { label: "Membership", to: "/membership", icon: CreditCard, testid: "menu-membership-link" },
  { label: "Notifications", to: "/command-center", icon: Bell, testid: "menu-notifications-link" },
  { label: "Account Settings", to: "/dashboard", icon: Settings, testid: "menu-settings-link" },
];
const ADMIN_LINKS = [
  { label: "Admin Dashboard", to: "/admin", icon: Shield, testid: "menu-admin-link" },
  { label: "CBR Live Admin", to: "/admin/live/games", icon: Radio, testid: "menu-admin-live-link" },
  { label: "Media Engine", to: "/admin/social", icon: Megaphone, testid: "menu-admin-media-link" },
  { label: "Membership Management", to: "/admin/payments", icon: CreditCard, testid: "menu-admin-membership-link" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const SoonRow = ({ label, testid, className }) => (
  <div data-testid={testid} className={className}>
    {label}
    <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-sm">Soon</span>
  </div>
);

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    setOpen(false);
    navigate("/");
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-2">
          {/* Logo */}
          <Link to="/" data-testid="logo-home-link" className="flex items-center group shrink-0">
            <span className="bg-[#0A192F] rounded-sm px-2.5 py-1.5 flex items-center">
              <img src="/cbr-logo.png" alt="CBR Prospects" className="h-7 w-auto" />
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden min-[1480px]:flex items-center gap-0 min-w-0">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="relative" onMouseEnter={() => setHover(item.label)} onMouseLeave={() => setHover(null)}>
                  <button
                    data-testid={`nav-${slug(item.label)}`}
                    aria-haspopup="true"
                    aria-expanded={hover === item.label}
                    onClick={() => setHover(hover === item.label ? null : item.label)}
                    className={`flex items-center gap-0.5 px-1.5 py-2 text-[13px] font-semibold tracking-wide uppercase whitespace-nowrap transition-colors ${hover === item.label ? "text-[#E63946]" : "text-[#0A192F] hover:text-[#E63946]"}`}
                  >
                    {item.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${hover === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {hover === item.label && (
                    <div data-testid={`nav-dropdown-${slug(item.label)}`} className="absolute left-0 top-full pt-1 w-60">
                      <div className="bg-white border border-slate-200 rounded-sm shadow-lg py-2">
                        {item.children.map((c) =>
                          c.comingSoon ? (
                            <SoonRow key={c.label} label={c.label} testid={`nav-item-${slug(c.label)}`}
                              className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-400 cursor-default" />
                          ) : (
                            <Link key={c.label} to={c.to} data-testid={`nav-item-${slug(c.label)}`}
                              className="block px-4 py-2.5 text-sm font-medium text-[#0A192F] hover:bg-slate-50 hover:text-[#E63946] transition-colors">
                              {c.label}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={item.label} to={item.to} end={item.end} data-testid={`nav-${slug(item.label)}`}
                  className={({ isActive }) =>
                    `px-1.5 py-2 text-[13px] font-semibold tracking-wide uppercase whitespace-nowrap transition-colors ${isActive ? "text-[#E63946]" : "text-[#0A192F] hover:text-[#E63946]"}`
                  }>
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Right side */}
          <div className="hidden min-[1480px]:flex items-center gap-1.5 shrink-0">
            <button data-testid="global-search-trigger" onClick={() => setSearchOpen(true)} aria-label="Search"
              className="flex items-center gap-2 px-2.5 py-2 border border-slate-200 rounded-sm text-slate-400 hover:border-slate-300 hover:text-slate-600 transition-colors">
              <Search className="w-4 h-4" />
              <kbd className="hidden 2xl:inline text-[10px] font-data bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">⌘K</kbd>
            </button>
            <CartButton />
            {!user ? (
              <>
                <Link to="/login" data-testid="header-login-link" className="px-3 py-2 text-sm font-semibold tracking-wide uppercase text-[#0A192F] hover:text-[#E63946] whitespace-nowrap">Login</Link>
                <Link to="/signup" data-testid="header-signup-link" className="px-4 py-2 bg-[#0A192F] text-white text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1a2a4a] transition-colors whitespace-nowrap">Join CBR</Link>
              </>
            ) : (
              <div className="relative">
                <button data-testid="user-menu-button" onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 px-3 py-2 border border-slate-200 hover:border-slate-300 rounded-sm">
                  <div className="w-7 h-7 bg-[#0A192F] text-white grid place-items-center text-xs font-bold rounded-sm">{user.name?.[0]?.toUpperCase() || "U"}</div>
                  <span className="text-sm font-semibold text-[#0A192F] max-w-[120px] truncate">{user.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-sm shadow-md py-2 max-h-[80vh] overflow-y-auto" onMouseLeave={() => setMenuOpen(false)}>
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-sm font-medium text-[#0A192F] truncate">{user.email}</p>
                      <p className="label-overline mt-1">{user.role.replace("_", " ")}</p>
                    </div>
                    {ACCOUNT_LINKS.map((l) => (
                      <Link key={l.label} to={l.to} data-testid={l.testid} onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#0A192F] hover:bg-slate-50 hover:text-[#E63946]">
                        <l.icon className="w-4 h-4 text-slate-400" /> {l.label}
                      </Link>
                    ))}
                    {(user.role === "evaluator" || user.role === "scout") && (
                      <Link to="/evaluator" data-testid="menu-evaluator-link" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#0A192F] hover:bg-slate-50">
                        <ClipboardList className="w-4 h-4 text-slate-400" /> Evaluator Console
                      </Link>
                    )}
                    {isAdmin && (
                      <>
                        <div className="px-4 pt-3 pb-1 label-overline text-slate-400 border-t border-slate-100 mt-1">Admin Tools</div>
                        {ADMIN_LINKS.map((l) => (
                          <Link key={l.label} to={l.to} data-testid={l.testid} onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#0A192F] hover:bg-slate-50 hover:text-[#E63946]">
                            <l.icon className="w-4 h-4 text-slate-400" /> {l.label}
                          </Link>
                        ))}
                      </>
                    )}
                    <button data-testid="menu-logout-button" onClick={handleLogout} className="flex items-center gap-2.5 px-4 py-2 mt-1 border-t border-slate-100 text-sm hover:bg-slate-50 w-full text-left text-[#E63946]">
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="min-[1480px]:hidden flex items-center gap-0.5">
            <button data-testid="mobile-global-search-trigger" onClick={() => setSearchOpen(true)} className="p-1.5 text-[#0A192F]"><Search className="w-5 h-5" /></button>
            <CartButton mobile />
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-1.5 text-[#0A192F]">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="min-[1480px]:hidden py-4 border-t border-slate-200 max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col gap-1">
              {NAV.map((item) =>
                item.children ? (
                  <div key={item.label}>
                    <button data-testid={`mobile-nav-${slug(item.label)}`} onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold uppercase text-[#0A192F]">
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${expanded === item.label ? "rotate-180" : ""}`} />
                    </button>
                    {expanded === item.label && (
                      <div className="pl-4 border-l border-slate-200 ml-3 flex flex-col">
                        {item.children.map((c) =>
                          c.comingSoon ? (
                            <SoonRow key={c.label} label={c.label} testid={`mobile-nav-item-${slug(c.label)}`}
                              className="flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-400" />
                          ) : (
                            <Link key={c.label} to={c.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-item-${slug(c.label)}`}
                              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#E63946]">
                              {c.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink key={item.label} to={item.to} end={item.end} onClick={() => setOpen(false)} data-testid={`mobile-nav-${slug(item.label)}`}
                    className={({ isActive }) => `px-3 py-2 text-sm font-semibold uppercase ${isActive ? "text-[#E63946]" : "text-[#0A192F]"}`}>
                    {item.label}
                  </NavLink>
                )
              )}
              <div className="border-t border-slate-200 mt-2 pt-2">
                {!user ? (
                  <div className="flex gap-2 px-3">
                    <Link to="/login" onClick={() => setOpen(false)} className="flex-1 px-4 py-2 border border-slate-300 text-sm font-semibold uppercase text-center rounded-sm">Login</Link>
                    <Link to="/signup" onClick={() => setOpen(false)} className="flex-1 px-4 py-2 bg-[#0A192F] text-white text-sm font-semibold uppercase text-center rounded-sm">Join CBR</Link>
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5 px-3">
                    <div className="label-overline text-slate-400 px-0 py-1">My Account</div>
                    {ACCOUNT_LINKS.map((l) => (
                      <Link key={l.label} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}`} className="flex items-center gap-2.5 px-1 py-2 text-sm font-medium text-[#0A192F]">
                        <l.icon className="w-4 h-4 text-slate-400" /> {l.label}
                      </Link>
                    ))}
                    {isAdmin && ADMIN_LINKS.map((l) => (
                      <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="flex items-center gap-2.5 px-1 py-2 text-sm font-medium text-[#0A192F]">
                        <l.icon className="w-4 h-4 text-slate-400" /> {l.label}
                      </Link>
                    ))}
                    <button onClick={handleLogout} className="flex items-center gap-2.5 px-1 py-2 text-sm text-left text-[#E63946]"><LogOut className="w-4 h-4" /> Logout</button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
    </header>
  );
}
